import { auth, db } from '../../src/firebase.js';
import { collection, query, orderBy, onSnapshot, doc, getDoc, getDocs } from "firebase/firestore";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { sidebarPage } from '../utils/sidebarPageLayout.js';

// ─── Pricing config — update prices/Stripe links when ready to launch ───────
const PLANS = [
  {
    id: 'fan',
    name: 'Fan',
    price: '€3.99',
    period: 'mo',
    messages: 50,
    label: '50 messages/month',
    color: '#6c757d',
    highlight: false,
    stripeLink: '', // add Stripe payment link when ready
    features: ['Women\'s football chat', 'Tactics & formations', 'Conversation history'],
  },
  {
    id: 'starter',
    name: 'Starter',
    price: '€6.99',
    period: 'mo',
    messages: 200,
    label: '200 messages/month',
    color: '#4285f4',
    highlight: false,
    stripeLink: '',
    features: ['Everything in Fan', 'Player & team stats', 'League analysis'],
  },
  {
    id: 'plus',
    name: 'Plus',
    price: '€12.99',
    period: 'mo',
    messages: 600,
    label: '600 messages/month',
    color: '#e91e8c',
    highlight: true, // most popular
    stripeLink: '',
    features: ['Everything in Starter', 'Coaching deep-dives', 'Priority responses'],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '€15.99',
    period: 'mo',
    messages: -1,
    label: 'Unlimited messages',
    color: '#1a1a2e',
    highlight: false,
    stripeLink: '',
    features: ['Everything in Plus', 'Unlimited messages', 'Early access to new features'],
  },
];

const PLAN_LIMITS = { fan: 50, starter: 200, plus: 600, pro: -1 };

export class TiagoAI {
  constructor(containerId) {
    this.containerId = containerId;
    this.currentConversationId = `chat_${Date.now()}`;
    this.conversations = [];
    this.messages = [];
    this.userData = { plan: null, messageCount: 0 };
    this.isTyping = false;
    this.sidebarOpen = true;
    this.searchQuery = '';
    this.isDarkMode = false;
    this.isLaunched = false; // ← SET TO true WHEN YOU'RE READY TO OPEN
  }

  async init() {
    const el = document.getElementById(this.containerId);
    if (!el || !auth.currentUser) return;

    this.injectStyles();
    await this.fetchUserData();
    this.render();
    if (this.userData.plan) this.setupHistoryListener();
  }

  async fetchUserData() {
    const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
    if (userDoc.exists()) {
      this.userData = { ...this.userData, ...userDoc.data() };
    }
  }

  setupHistoryListener() {
    const q = query(
      collection(db, "users", auth.currentUser.uid, "conversations"),
      orderBy("updatedAt", "desc")
    );
    onSnapshot(q, (snap) => {
      this.conversations = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      this.updateSidebar();
    });
  }

  updateSidebar() {
    const list = document.getElementById('chat-history-list');
    if (!list) return;
    const filtered = this.conversations.filter(c =>
      (c.title || 'New Chat').toLowerCase().includes(this.searchQuery.toLowerCase())
    );
    list.innerHTML = filtered.map(c => `
      <div class="chat-history-item ${c.id === this.currentConversationId ? 'active' : ''}" data-id="${c.id}">
        <span class="chat-title">${c.title || 'New Chat'}</span>
      </div>
    `).join('');
    list.querySelectorAll('.chat-history-item').forEach(item => {
      item.onclick = () => this.loadConversation(item.dataset.id);
    });
  }

  async loadConversation(id) {
    this.currentConversationId = id;
    const messagesRef = collection(db, "users", auth.currentUser.uid, "conversations", id, "messages");
    const q = query(messagesRef, orderBy("createdAt", "asc"));
    const snap = await getDocs(q);
    this.messages = snap.docs.map(d => d.data());
    this.renderMessages();
    this.updateSidebar();
  }

  // ─── Main render ──────────────────────────────────────────────────────────

  render() {
    const el = document.getElementById(this.containerId);
    const hasPlan = !!this.userData.plan;
    const plan = PLANS.find(p => p.id === this.userData.plan);
    const limit = hasPlan ? PLAN_LIMITS[this.userData.plan] : 0;
    const count = this.userData.messageCount || 0;
    const quotaPct = limit === -1 ? 100 : Math.min((count / limit) * 100, 100);

    // Coming soon screen — shown when isLaunched = false AND user has no plan
    if (!this.isLaunched && !hasPlan) {
      const main = this._comingSoonTemplate();
      el.innerHTML = sidebarPage({ title: '', main, wide: true, pageClass: 'tiago-page' });
      this._attachPricingListeners();
      return;
    }

    // Paywall screen — launched but user has no plan
    if (this.isLaunched && !hasPlan) {
      const main = this._paywallTemplate();
      el.innerHTML = sidebarPage({ title: '', main, wide: true, pageClass: 'tiago-page' });
      this._attachPricingListeners();
      return;
    }

    // Full chat UI — user has a plan
    const main = `
      <div class="tiago-container ${this.sidebarOpen ? 'sidebar-open' : ''} ${this.isDarkMode ? 'dark-mode' : ''}">
        <header class="tiago-header">
          <img src="/images/tiago.png" class="tiago-trigger" id="sidebar-toggle" title="Toggle Sidebar">
        </header>

        <aside class="tiago-sidebar">
          <div class="sidebar-header">
            <button id="new-chat-btn" class="tiago-new-btn">+ New chat</button>
            <div class="sidebar-search">
              <input type="text" id="history-search" placeholder="Search history..." value="${this.searchQuery}">
            </div>
          </div>
          <div id="chat-history-list" class="chat-history-list"></div>
          <div class="sidebar-footer">
            <div class="tiago-quota-box">
              <div class="quota-header">
                <span class="quota-plan-badge" style="background:${plan?.color || '#888'}">${plan?.name || ''}</span>
                <span class="quota-text">${limit === -1 ? 'Unlimited' : `${count}/${limit} messages`}</span>
              </div>
              ${limit !== -1 ? `<div class="quota-bar"><div class="quota-fill" style="width:${quotaPct}%"></div></div>` : ''}
              <button class="quota-upgrade-btn" id="show-plans-btn">Manage plan</button>
            </div>
            <button id="theme-toggle" class="theme-toggle-btn">
              ${this.isDarkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
            </button>
          </div>
        </aside>

        <main class="tiago-main">
          <div id="chat-window" class="chat-window">
            <div class="chat-welcome">
              <img src="/images/tiago.png" class="welcome-avatar">
              <h1>How can Tiago help you today?</h1>
              <p class="welcome-intro">👋 Hi! I'm Tiago, your women's football expert. Ask me anything about the game — tactics, players, leagues or coaching. I'm here to help! ⚽</p>
              <div class="tiago-suggestions">
                <button class="tiago-suggestion-chip" data-query="What formations work best in women's football?">⚽ What formations work best in women's football?</button>
                <button class="tiago-suggestion-chip" data-query="Who are the best young players in the WSL right now?">🏆 Best young WSL players right now?</button>
                <button class="tiago-suggestion-chip" data-query="How do I coach a high press effectively?">🧠 How do I coach a high press?</button>
              </div>
            </div>
          </div>
          <div class="chat-input-area">
            <div class="input-wrapper">
              <input type="text" id="chat-input" placeholder="Ask Tiago about women's football..." autocomplete="off">
              <button id="send-btn" class="send-btn">➤</button>
            </div>
            <p class="tiago-disclaimer">Tiago can make mistakes. Check important info.</p>
          </div>
        </main>
      </div>

      ${this._upgradeModalTemplate()}
    `;

    el.innerHTML = sidebarPage({ title: '', main, wide: true, pageClass: 'tiago-page' });
    this.attachListeners();
    this.renderMessages();
    this.updateSidebar();
  }

  // ─── Templates ────────────────────────────────────────────────────────────

  _pricingCardsHTML(context = 'page') {
    return PLANS.map(p => `
      <div class="tiago-plan-card ${p.highlight ? 'highlighted' : ''}">
        ${p.highlight ? '<div class="plan-popular-badge">Most Popular</div>' : ''}
        <div class="plan-name" style="color:${p.color}">${p.name}</div>
        <div class="plan-price">${p.price}<span class="plan-period">/${p.period}</span></div>
        <div class="plan-messages">${p.label}</div>
        <ul class="plan-features">
          ${p.features.map(f => `<li>✓ ${f}</li>`).join('')}
        </ul>
        <button class="plan-cta-btn ${p.highlight ? 'plan-cta-primary' : ''}"
          data-plan="${p.id}"
          data-link="${p.stripeLink}"
          style="${p.highlight ? `background:${p.color};` : `border-color:${p.color};color:${p.color};`}">
          ${context === 'coming-soon' ? 'Join at launch' : 'Get started'}
        </button>
      </div>
    `).join('');
  }

  _comingSoonTemplate() {
    return `
      <div class="tiago-pricing-page">
        <div class="tiago-cs-hero">
          <img src="/images/tiago.png" class="tiago-cs-avatar">
          <h1 class="tiago-cs-title">Tiago AI is coming soon</h1>
          <p class="tiago-cs-sub">Your expert women's football assistant — tactics, players, leagues, coaching. Launching next month. Pick your plan now.</p>
          <div class="tiago-cs-badge">🚀 Launching soon</div>
        </div>
        <div class="tiago-plans-grid">
          ${this._pricingCardsHTML('coming-soon')}
        </div>
        <p class="tiago-pricing-note">All plans billed monthly. Cancel anytime. Prices in EUR.</p>
      </div>
    `;
  }

  _paywallTemplate() {
    return `
      <div class="tiago-pricing-page">
        <div class="tiago-cs-hero">
          <img src="/images/tiago.png" class="tiago-cs-avatar">
          <h1 class="tiago-cs-title">Meet Tiago</h1>
          <p class="tiago-cs-sub">Your expert women's football assistant. Choose a plan to start chatting about tactics, players, leagues and coaching.</p>
        </div>
        <div class="tiago-plans-grid">
          ${this._pricingCardsHTML('paywall')}
        </div>
        <p class="tiago-pricing-note">All plans billed monthly. Cancel anytime. Prices in EUR.</p>
      </div>
    `;
  }

  _upgradeModalTemplate() {
    return `
      <div id="upgrade-modal" class="fg-modal-overlay">
        <div class="fg-modal tiago-upgrade-modal">
          <button class="tiago-modal-close" onclick="document.getElementById('upgrade-modal').classList.remove('is-open')">✕</button>
          <img src="/images/tiago.png" class="tiago-modal-avatar">
          <h2>Upgrade your plan</h2>
          <p>You've used all your messages this month. Upgrade to keep chatting with Tiago.</p>
          <div class="tiago-plans-grid compact">
            ${this._pricingCardsHTML('modal')}
          </div>
        </div>
      </div>
    `;
  }

  // ─── Event listeners ──────────────────────────────────────────────────────

  _attachPricingListeners() {
    document.querySelectorAll('.plan-cta-btn').forEach(btn => {
      btn.onclick = () => {
        const link = btn.dataset.link;
        if (link) {
          window.location.href = link;
        } else {
          // No Stripe link yet — show coming soon message
          btn.textContent = '✓ We\'ll notify you!';
          btn.disabled = true;
        }
      };
    });
  }

  attachListeners() {
    const input = document.getElementById('chat-input');
    const sendBtn = document.getElementById('send-btn');
    const newChatBtn = document.getElementById('new-chat-btn');
    const toggleBtn = document.getElementById('sidebar-toggle');
    const searchInput = document.getElementById('history-search');
    const themeBtn = document.getElementById('theme-toggle');
    const showPlansBtn = document.getElementById('show-plans-btn');

    const handleSend = (text) => {
      const msg = text || input?.value.trim();
      if (msg) this.sendMessage(msg);
    };

    sendBtn?.addEventListener('click', () => handleSend());
    input?.addEventListener('keypress', (e) => { if (e.key === 'Enter') handleSend(); });
    newChatBtn?.addEventListener('click', () => {
      this.currentConversationId = `chat_${Date.now()}`;
      this.messages = [];
      this.render();
    });
    toggleBtn?.addEventListener('click', () => {
      this.sidebarOpen = !this.sidebarOpen;
      document.querySelector('.tiago-container')?.classList.toggle('sidebar-open');
    });
    searchInput?.addEventListener('input', (e) => {
      this.searchQuery = e.target.value;
      this.updateSidebar();
    });
    themeBtn?.addEventListener('click', () => {
      this.isDarkMode = !this.isDarkMode;
      this.render();
    });
    showPlansBtn?.addEventListener('click', () => {
      document.getElementById('upgrade-modal')?.classList.add('is-open');
    });

    document.querySelectorAll('.tiago-suggestion-chip').forEach(chip => {
      chip.addEventListener('click', () => handleSend(chip.dataset.query));
    });

    this._attachPricingListeners();
  }

  // ─── Chat logic ───────────────────────────────────────────────────────────

  async sendMessage(text) {
    if (this.isTyping) return;

    // Check quota client-side before calling function
    const limit = PLAN_LIMITS[this.userData.plan] || 0;
    const count = this.userData.messageCount || 0;
    if (limit !== -1 && count >= limit) {
      document.getElementById('upgrade-modal')?.classList.add('is-open');
      return;
    }

    this.messages.push({ text, sender: 'user', createdAt: new Date() });
    this.renderMessages();
    const input = document.getElementById('chat-input');
    if (input) input.value = '';

    this.isTyping = true;
    this.addTypingIndicator();

    try {
      const functions = getFunctions();
      const chatFn = httpsCallable(functions, 'chat');
      const result = await chatFn({ message: text, conversationId: this.currentConversationId });

      this.removeTypingIndicator();
      this.messages.push({ text: result.data.text, sender: 'assistant' });
      this.userData.messageCount = result.data.currentCount;
      this.renderMessages();
      this._updateQuotaDisplay();
    } catch (error) {
      this.removeTypingIndicator();
      const code = error.details?.code || '';
      if (code === 'quota-exceeded' || code === 'subscription-required') {
        document.getElementById('upgrade-modal')?.classList.add('is-open');
      } else {
        this.messages.push({ text: 'Sorry, something went wrong. Please try again.', sender: 'assistant' });
        this.renderMessages();
        console.error("Chat Error:", error);
      }
    } finally {
      this.isTyping = false;
    }
  }

  _updateQuotaDisplay() {
    const plan = PLANS.find(p => p.id === this.userData.plan);
    const limit = PLAN_LIMITS[this.userData.plan] || 0;
    const count = this.userData.messageCount || 0;
    const quotaText = document.querySelector('.quota-text');
    const quotaFill = document.querySelector('.quota-fill');
    if (quotaText) quotaText.textContent = limit === -1 ? 'Unlimited' : `${count}/${limit} messages`;
    if (quotaFill && limit !== -1) quotaFill.style.width = `${Math.min((count / limit) * 100, 100)}%`;
  }

  renderMessages() {
    const windowEl = document.getElementById('chat-window');
    if (!windowEl || this.messages.length === 0) return;
    windowEl.innerHTML = this.messages.map(m => `
      <div class="message-row ${m.sender}">
        ${m.sender === 'assistant' ? '<img src="/images/tiago.png" class="msg-avatar">' : ''}
        <div class="msg-bubble">${m.text}</div>
      </div>
    `).join('');
    windowEl.scrollTop = windowEl.scrollHeight;
  }

  addTypingIndicator() {
    const windowEl = document.getElementById('chat-window');
    if (!windowEl) return;
    const div = document.createElement('div');
    div.id = 'typing-indicator';
    div.className = 'message-row assistant';
    div.innerHTML = '<img src="/images/tiago.png" class="msg-avatar"><div class="msg-bubble typing"><span></span><span></span><span></span></div>';
    windowEl.appendChild(div);
    windowEl.scrollTop = windowEl.scrollHeight;
  }

  removeTypingIndicator() {
    document.getElementById('typing-indicator')?.remove();
  }

  // ─── Styles ───────────────────────────────────────────────────────────────

  injectStyles() {
    if (document.getElementById('tiago-styles')) return;
    const css = `
      /* ── Base container ── */
      .tiago-container { display: flex; height: calc(100vh - 60px); margin: -24px; background: #fff; color: #1f1f1f; position: relative; }
      .tiago-container.dark-mode { background: #131314; color: #e3e3e3; }

      .tiago-header { position: absolute; top: 16px; left: 16px; z-index: 100; }
      .tiago-trigger { width: 40px; height: 40px; border-radius: 50%; cursor: pointer; border: 2px solid transparent; transition: border-color 0.2s; background: #fff; }
      .tiago-trigger:hover { border-color: #e91e8c; }

      /* ── Sidebar ── */
      .tiago-sidebar { width: 0; background: #f8f9fa; display: flex; flex-direction: column; overflow: hidden; transition: width 0.3s ease; border-right: 1px solid #e0e0e0; }
      .sidebar-open .tiago-sidebar { width: 280px; padding: 16px; }
      .dark-mode .tiago-sidebar { background: #1e1f20; border-right-color: #333; }
      .sidebar-header { margin-top: 50px; }
      .tiago-new-btn { width: 100%; background: #e8eaed; border: none; color: #1f1f1f; padding: 12px 20px; border-radius: 24px; text-align: left; font-weight: 500; cursor: pointer; margin-bottom: 16px; }
      .dark-mode .tiago-new-btn { background: #28292a; color: #e3e3e3; }
      .sidebar-search input { width: 100%; padding: 8px 16px; border-radius: 8px; border: 1px solid #ddd; background: #fff; margin-bottom: 16px; outline: none; box-sizing: border-box; }
      .dark-mode .sidebar-search input { background: #131314; border-color: #444; color: #fff; }
      .chat-history-list { flex: 1; overflow-y: auto; margin-bottom: 16px; }
      .chat-history-item { padding: 10px 12px; border-radius: 8px; cursor: pointer; font-size: 0.85rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: #444; }
      .chat-history-item:hover { background: #e8eaed; }
      .chat-history-item.active { background: #fce4f3; color: #c2185b; font-weight: 600; }
      .dark-mode .chat-history-item { color: #aaa; }
      .dark-mode .chat-history-item.active { background: #3c4043; color: #f48fb1; }

      /* ── Quota box ── */
      .sidebar-footer { border-top: 1px solid #ddd; padding-top: 16px; }
      .dark-mode .sidebar-footer { border-top-color: #333; }
      .tiago-quota-box { padding: 12px; background: #f0f2f5; border-radius: 12px; margin-bottom: 8px; }
      .dark-mode .tiago-quota-box { background: #28292a; }
      .quota-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
      .quota-plan-badge { font-size: 0.7rem; color: #fff; padding: 2px 8px; border-radius: 10px; font-weight: 700; }
      .quota-text { font-size: 0.78rem; color: #666; }
      .dark-mode .quota-text { color: #aaa; }
      .quota-bar { height: 4px; background: #ddd; border-radius: 2px; margin-bottom: 10px; }
      .dark-mode .quota-bar { background: #3c4043; }
      .quota-fill { height: 100%; background: #e91e8c; border-radius: 2px; transition: width 0.4s; }
      .quota-upgrade-btn { width: 100%; background: none; border: 1px solid #e91e8c; color: #e91e8c; border-radius: 8px; padding: 6px; font-size: 0.78rem; cursor: pointer; }
      .quota-upgrade-btn:hover { background: #fce4f3; }
      .theme-toggle-btn { width: 100%; background: none; border: 1px solid #ddd; padding: 8px; border-radius: 8px; cursor: pointer; font-size: 0.8rem; color: inherit; }

      /* ── Chat main ── */
      .tiago-main { flex: 1; display: flex; flex-direction: column; padding: 0 8%; overflow: hidden; }
      .chat-window { flex: 1; overflow-y: auto; padding: 40px 0; display: flex; flex-direction: column; gap: 32px; }
      .chat-welcome { text-align: center; margin-top: 8vh; max-width: 700px; margin-left: auto; margin-right: auto; }
      .welcome-avatar { width: 64px; height: 64px; border-radius: 50%; margin-bottom: 16px; }
      .chat-welcome h1 { font-size: 2.2rem; background: linear-gradient(90deg, #e91e8c, #4285f4); -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 12px; }
      .welcome-intro { color: #5f6368; line-height: 1.6; margin-bottom: 32px; }
      .dark-mode .welcome-intro { color: #aaa; }
      .tiago-suggestions { display: flex; flex-direction: column; gap: 8px; align-items: center; }
      .tiago-suggestion-chip { background: #f0f2f5; border: 1px solid #ddd; color: #1f1f1f; padding: 12px 20px; border-radius: 12px; cursor: pointer; width: 100%; max-width: 450px; text-align: left; font-size: 0.9rem; }
      .tiago-suggestion-chip:hover { background: #fce4f3; border-color: #e91e8c; }
      .dark-mode .tiago-suggestion-chip { background: #1e1f20; border-color: #3c4043; color: #e3e3e3; }

      /* ── Messages ── */
      .message-row { display: flex; gap: 16px; max-width: 800px; width: 100%; align-self: center; }
      .message-row.user { justify-content: flex-end; }
      .msg-avatar { width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0; }
      .msg-bubble { line-height: 1.6; font-size: 1rem; }
      .user .msg-bubble { background: #f0f2f5; padding: 12px 20px; border-radius: 18px; max-width: 80%; color: #1f1f1f; }
      .dark-mode .user .msg-bubble { background: #28292a; color: #e3e3e3; }

      /* ── Typing indicator ── */
      .typing { display: flex; gap: 4px; align-items: center; padding: 8px 0; }
      .typing span { width: 8px; height: 8px; background: #e91e8c; border-radius: 50%; animation: bounce 1.2s infinite; }
      .typing span:nth-child(2) { animation-delay: 0.2s; }
      .typing span:nth-child(3) { animation-delay: 0.4s; }
      @keyframes bounce { 0%,60%,100% { transform: translateY(0); } 30% { transform: translateY(-6px); } }

      /* ── Input ── */
      .chat-input-area { padding: 20px 0 40px; width: 100%; max-width: 800px; align-self: center; flex-shrink: 0; }
      .input-wrapper { background: #f0f2f5; border-radius: 28px; padding: 8px 24px; display: flex; align-items: center; gap: 12px; border: 1px solid #ddd; }
      .dark-mode .input-wrapper { background: #1e1f20; border-color: #333; }
      .input-wrapper input { flex: 1; background: none; border: none; color: inherit; height: 48px; font-size: 1rem; outline: none; }
      .send-btn { background: none; border: none; font-size: 1.2rem; cursor: pointer; color: #e91e8c; }
      .tiago-disclaimer { font-size: 0.75rem; color: #9aa0a6; text-align: center; margin-top: 12px; }

      /* ── Pricing page ── */
      .tiago-pricing-page { max-width: 1100px; margin: 0 auto; padding: 48px 24px 64px; }
      .tiago-cs-hero { text-align: center; margin-bottom: 48px; }
      .tiago-cs-avatar { width: 80px; height: 80px; border-radius: 50%; margin-bottom: 20px; border: 3px solid #e91e8c; }
      .tiago-cs-title { font-size: 2.4rem; font-weight: 700; margin-bottom: 12px; background: linear-gradient(90deg, #e91e8c, #4285f4); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .tiago-cs-sub { font-size: 1.1rem; color: #666; max-width: 560px; margin: 0 auto 20px; line-height: 1.6; }
      .tiago-cs-badge { display: inline-block; background: #fce4f3; color: #c2185b; padding: 6px 16px; border-radius: 20px; font-size: 0.85rem; font-weight: 600; }

      .tiago-plans-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 24px; }
      @media (max-width: 900px) { .tiago-plans-grid { grid-template-columns: repeat(2, 1fr); } }
      @media (max-width: 560px) { .tiago-plans-grid { grid-template-columns: 1fr; } }

      .tiago-plan-card { background: #fff; border: 1.5px solid #e0e0e0; border-radius: 16px; padding: 28px 20px; position: relative; transition: transform 0.2s, box-shadow 0.2s; }
      .tiago-plan-card:hover { transform: translateY(-4px); }
      .tiago-plan-card.highlighted { border-color: #e91e8c; border-width: 2px; }
      .plan-popular-badge { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: #e91e8c; color: #fff; font-size: 0.72rem; font-weight: 700; padding: 4px 14px; border-radius: 20px; white-space: nowrap; }
      .plan-name { font-size: 1rem; font-weight: 700; margin-bottom: 12px; }
      .plan-price { font-size: 2rem; font-weight: 800; color: #1f1f1f; margin-bottom: 4px; }
      .plan-period { font-size: 1rem; font-weight: 400; color: #888; }
      .plan-messages { font-size: 0.82rem; color: #888; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid #f0f0f0; }
      .plan-features { list-style: none; padding: 0; margin: 0 0 24px; display: flex; flex-direction: column; gap: 8px; }
      .plan-features li { font-size: 0.85rem; color: #444; }
      .plan-cta-btn { width: 100%; padding: 12px; border-radius: 10px; font-weight: 600; font-size: 0.9rem; cursor: pointer; border: 1.5px solid #ddd; background: transparent; transition: opacity 0.2s; }
      .plan-cta-btn:hover { opacity: 0.85; }
      .plan-cta-primary { color: #fff !important; }
      .plan-cta-btn:disabled { opacity: 0.6; cursor: default; }
      .tiago-pricing-note { text-align: center; color: #aaa; font-size: 0.8rem; }

      /* ── Upgrade modal ── */
      .fg-modal-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 1000; align-items: center; justify-content: center; overflow-y: auto; padding: 24px; }
      .fg-modal-overlay.is-open { display: flex; }
      .tiago-upgrade-modal { background: #fff; border-radius: 20px; padding: 40px 32px; max-width: 960px; width: 100%; position: relative; text-align: center; }
      .tiago-modal-close { position: absolute; top: 16px; right: 16px; background: none; border: none; font-size: 1.2rem; cursor: pointer; color: #888; }
      .tiago-modal-avatar { width: 56px; height: 56px; border-radius: 50%; margin-bottom: 16px; }
      .tiago-upgrade-modal h2 { margin-bottom: 8px; }
      .tiago-upgrade-modal p { color: #666; margin-bottom: 32px; }
      .tiago-plans-grid.compact { gap: 12px; }
      .tiago-plans-grid.compact .tiago-plan-card { padding: 20px 14px; }
      .tiago-plans-grid.compact .plan-price { font-size: 1.6rem; }
    `;
    const style = document.createElement('style');
    style.id = 'tiago-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }
}